import React, { memo, useEffect, useState, ChangeEvent, useCallback } from 'react'
import { DatePicker } from 'components/forms/DatePicker'
import moment, { Moment } from 'moment-timezone'
import { TIME_INPUT_FORMAT, TimeInput, buildDateTime } from 'transfix-ui/components/TimeInput'
import { TIME_TYPE, timeTypeOptions } from 'apps/fleet-planner/constants/loads'
import { IStop } from 'apps/fleet-planner/types/loads'
import { Checkbox } from 'components/forms'
import { FORMATS } from 'constants/datetime'

export enum APPOINTMENT_VARIANT {
  LOAD_DETAILS = 'load-details',
  CREATE_LOAD = 'create-load',
}

interface IAppointmentProps {
  stop: Partial<IStop>
  onStopChange: (stop: Partial<IStop>) => void
  variant: APPOINTMENT_VARIANT
  // TODO, define errors type in FP-796
  errors?: any
  stopIndex: number
  id?: string
  timeFormat: TIME_INPUT_FORMAT
}

const getFormattedDateTime = (dateTimeFormat: string, useIsoFormat: boolean, dateTime?: Moment) => {
  if (!dateTime && !dateTimeFormat) return undefined
  if (useIsoFormat) return dateTime?.toISOString()
  return dateTime?.format(dateTimeFormat)
}

const getClosestTime = (addHours: number = 0, timeFormat: TIME_INPUT_FORMAT): string => {
  const now = moment()
  return now
    .add(now.minutes() > 30 ? addHours + 1 : addHours, 'h')
    .minutes(now.minutes() > 30 ? 0 : 30)
    .format(timeFormat)
}

export const Appointment = memo<IAppointmentProps>(
  ({ stop, onStopChange, variant, errors, id, timeFormat = TIME_INPUT_FORMAT.MILITARY_SHORT }) => {
    const isLoadDetails = variant === APPOINTMENT_VARIANT.LOAD_DETAILS
    const className = isLoadDetails ? 'pl0' : ''
    const { displayTimezone, endAt, startAt, stopSequence, stopType } = stop
    const initialTZ = displayTimezone || moment.tz.guess()
    const appointmentIsRange = startAt && endAt
    const [timeType, setTimeType] = useState(
      appointmentIsRange ? timeTypeOptions[1].value : timeTypeOptions[0].value
    )

    const [stopStartDate, setStopStartDate] = useState(
      moment(startAt || undefined)
        .tz(initialTZ)
        .format(FORMATS.flatpickrDateTimeFormat)
    )

    const [stopStartTime, setStopStartTime] = useState(
      startAt ? moment(startAt).tz(initialTZ).format(timeFormat) : getClosestTime(0, timeFormat)
    )

    const [stopEndDate, setStopEndDate] = useState(
      moment(endAt || startAt || undefined)
        .tz(initialTZ)
        .format(FORMATS.flatpickrDateTimeFormat)
    )

    const [stopEndTime, setStopEndTime] = useState(
      endAt || startAt
        ? moment(endAt || startAt)
            .tz(initialTZ)
            .format(timeFormat)
        : getClosestTime(1, timeFormat)
    )

    const onStartDateChangeHandler = useCallback(
      (dates: string[]) => {
        if (!dates[0]) {
          setStopStartDate('')
        } else if (!moment(stopStartDate).isSame(dates[0], 'd')) {
          setStopStartDate(moment(dates[0]).format(FORMATS.flatpickrDateTimeFormat))
          // We do not want to pass timezones here on create-load
          const newStartDateMoment = buildDateTime(
            dates[0],
            stopStartTime,
            timeFormat,
            isLoadDetails ? initialTZ : undefined
          )
          const oldEndDateMoment = buildDateTime(
            stopEndDate,
            stopStartTime,
            timeFormat,
            isLoadDetails ? initialTZ : undefined
          )
          if (
            oldEndDateMoment &&
            newStartDateMoment &&
            oldEndDateMoment.diff(newStartDateMoment, 'h') < 1
          ) {
            setStopEndDate(newStartDateMoment.format(FORMATS.flatpickrDateTimeFormat))
            setStopEndTime(newStartDateMoment.clone().add(1, 'hours').format(timeFormat))
          }
        }
      },
      [stopEndDate, stopStartDate, stopStartTime]
    )

    const onStartTimeChangeHandler = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setStopStartTime(value)
        // We do not want to pass timezones here on create-load
        const newStopStartDateMoment = buildDateTime(
          stopStartDate,
          value,
          timeFormat,
          isLoadDetails ? initialTZ : undefined
        )
        const oldEndDateMoment = buildDateTime(
          stopEndDate,
          stopEndTime,
          timeFormat,
          isLoadDetails ? initialTZ : undefined
        )
        if (
          oldEndDateMoment &&
          newStopStartDateMoment &&
          oldEndDateMoment?.diff(newStopStartDateMoment, 'h') < 1
        ) {
          setStopEndDate(newStopStartDateMoment.format(FORMATS.flatpickrDateTimeFormat))
          setStopEndTime(newStopStartDateMoment.clone().add(1, 'hours').format(timeFormat))
        }
      },
      [stopEndDate, stopEndTime, stopStartDate]
    )

    const onEndDateChangeHandler = useCallback(
      (dates: string[]) => {
        if (!dates[0]) {
          setStopEndDate('')
        } else if (!moment(stopEndDate).isSame(dates[0], 'd')) {
          setStopEndDate(moment(dates[0]).format(FORMATS.flatpickrDateTimeFormat))
        }
      },
      [stopEndDate]
    )

    const onEndTimeChangeHandler = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setStopEndTime(value)
      },
      []
    )

    const onChangeAppointmentTypeHandler = ({
      target: { checked },
    }: ChangeEvent<HTMLInputElement>) => {
      setTimeType(checked ? TIME_TYPE.window : TIME_TYPE.appointment)
    }

    const formatStopTimes = (date: string, time: string) => {
      const dateTime = buildDateTime(date, time, timeFormat, initialTZ)
      const formattedDateTime = getFormattedDateTime(
        FORMATS.fleetPlannerApiFormat,
        isLoadDetails,
        dateTime
      )
      return formattedDateTime
    }

    // When `timeType` ( appointment | window ) changes
    useEffect(() => {
      if (timeType === TIME_TYPE.appointment) {
        onStopChange({
          endAt: '',
        })
      } else if (timeType === TIME_TYPE.window && !endAt) {
        // We do not want to pass timezones here on create-load
        const endDateTime = buildDateTime(
          stopStartDate,
          stopStartTime,
          timeFormat,
          isLoadDetails ? initialTZ : undefined
        )
        if (endDateTime) {
          endDateTime.add(1, 'h')
          setStopEndDate(endDateTime.format(FORMATS.flatpickrDateTimeFormat))
          setStopEndTime(endDateTime.format(timeFormat))
        }

        // Add this value to the stop object
        onStopChange({
          endAt: formatStopTimes(stopEndDate, stopEndTime),
        })
      }
    }, [timeType])

    // when `stopStartTime` or `stopStartDate` changes, update the `stop`
    useEffect(() => {
      onStopChange({
        startAt: formatStopTimes(stopStartDate, stopStartTime),
      })
    }, [stopStartTime, stopStartDate])

    // When `stopEndTime` or `stopEndDate` changes, update the stop
    useEffect(() => {
      if (timeType === TIME_TYPE.window) {
        onStopChange({
          endAt: formatStopTimes(stopEndDate, stopEndTime),
        })
      } else if (timeType === TIME_TYPE.appointment) {
        // Clear the `endAt` value if it's an `appointment`
        onStopChange({
          endAt: '',
        })
      }
    }, [stopEndTime, moment(stopEndDate).format(FORMATS.dateFormat)])

    return (
      <>
        <div className='cf'>
          <div className={`col-xs-6 col-md-4 ${className} pl0 pl2-m`}>
            <DatePicker
              date={stopStartDate}
              placeholder='Date'
              onDateChange={onStartDateChangeHandler}
              dataTest={`${id}-stop-start-date-${stopType}-${stopSequence}`}
              id={`${id}-stop-start-at-date-${stopType}-${stopSequence}`}
              ariaLabel={`${id}-stop-start-date-${stopType}-${stopSequence}`}
              large
              validationMessage={errors?.startAt || ''}
              invalid={!!errors?.startAt}
              wrapValidationText={false}
            />
          </div>
          <div className='fl'>
            <TimeInput
              timeFormat={timeFormat}
              value={stopStartTime}
              onChange={onStartTimeChangeHandler}
              dataTest={`${id}-stop-start-at-time-${stopType}-${stopSequence}`}
              ariaLabel={`${id}-stop-start-at-time-${stopType}-${stopSequence}`}
              invalid={!!errors?.startAt}
              large
            />
          </div>
          {(timeType === TIME_TYPE.window || appointmentIsRange) && (
            <>
              <div className='fl ml2 mt2'>&nbsp;to&nbsp;</div>
              <div className='col-xs-6 col-md-4'>
                <DatePicker
                  date={stopEndDate}
                  placeholder='Date'
                  onDateChange={onEndDateChangeHandler}
                  dataTest={`${id}-stop-end-date-${stop.stopSequence}`}
                  id={`${id}-stop-end-at-date-${stopType}-${stopSequence}`}
                  invalid={!!errors?.endAt}
                  validationMessage={errors?.endAt || ''}
                  wrapValidationText={false}
                  large
                />
              </div>
              <div className='fl'>
                <TimeInput
                  timeFormat={timeFormat}
                  value={stopEndTime}
                  onChange={onEndTimeChangeHandler}
                  targetDate={stopEndDate}
                  anchorDateTime={{ date: stopStartDate, time: stopStartTime }}
                  dataTest={`${id}-stop-end-at-time-${stopType}-${stopSequence}`}
                  invalid={!!errors?.endAt}
                  large
                />
              </div>
            </>
          )}
        </div>
        <div className='cf'>
          <div className={`col-xs-12 mt2 ${className} pl0 pl2-m`}>
            <Checkbox
              label={TIME_TYPE.window}
              checked={timeType === TIME_TYPE.window}
              onChange={onChangeAppointmentTypeHandler}
              id={`${id}-stop-time-type-${stopType}-${stopSequence}`}
              dataTest={`${id}-stop-time-type-${stopType}-${stopSequence}`}
            />
          </div>
        </div>
      </>
    )
  }
)
